import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, CheckCircle, Clock, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { formatMonth } from './useContributorDashboard';

const EarningsTab = ({ payouts }) => {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const downloadSummary = async (year) => {
    try {
      const response = await axios.get(
        `${API}/contributors/my-earnings-summary/${year}`,
        { headers }
      );
      const data = response.data;
      
      // Create downloadable text content
      let content = `ANNUAL EARNINGS SUMMARY - ${year}\n`;
      content += `${'='.repeat(50)}\n\n`;
      content += `Generated: ${new Date(data.generated_at).toLocaleDateString()}\n\n`;
      content += `CONTRIBUTOR INFORMATION\n`;
      content += `-`.repeat(30) + `\n`;
      content += `Name: ${data.contributor.name}\n`;
      content += `Email: ${data.contributor.email}\n`;
      if (data.contributor.address?.street) {
        content += `Address: ${data.contributor.address.street}\n`;
        content += `         ${data.contributor.address.city}, ${data.contributor.address.state} ${data.contributor.address.zip}\n`;
      }
      content += `\nEARNINGS SUMMARY\n`;
      content += `-`.repeat(30) + `\n`;
      content += `Total Earnings: $${data.summary.total_earnings.toFixed(2)}\n`;
      content += `Total Points Earned: ${data.summary.total_points}\n`;
      content += `Months Eligible: ${data.summary.months_eligible}\n`;
      content += `Payments Received: ${data.summary.payout_count}\n`;
      content += `\nMONTHLY BREAKDOWN\n`;
      content += `-`.repeat(30) + `\n`;
      data.monthly_breakdown.forEach(m => {
        const monthName = new Date(m.month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        content += `${monthName}: $${m.amount.toFixed(2)} (${m.points} pts, ${(m.share_percentage * 100).toFixed(1)}% share)`;
        if (m.check_number) content += ` - Check #${m.check_number}`;
        content += `\n`;
      });
      content += `\n${'='.repeat(50)}\n`;
      content += `\nDISCLAIMER:\n${data.disclaimer}\n`;
      
      // Download as text file
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `earnings_summary_${year}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${year} earnings summary`);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.info(`No earnings data for ${year}`);
      } else {
        toast.error('Failed to download summary');
      }
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Direct deposit moved to the Practitioner Hub Overview (owner, Jun 2026):
          "I cannot find it anywhere!" — it was buried in here, three tabs deep, where no
          practitioner or storefront owner would think to look. The mailing address for paper
          checks moved with it, since they are the same decision. */}
      {/* Annual Summary Download */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Annual Earnings Summary
          </CardTitle>
          <CardDescription className="text-gray-400">
            Download a summary of your earnings for tax purposes
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {[new Date().getFullYear(), new Date().getFullYear() - 1].map(year => (
              <Button
                key={year}
                variant="outline"
                onClick={() => downloadSummary(year)}
              >
                <Calendar className="w-4 h-4 mr-2" />
                {year} Summary
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Payout History */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-white">Payout History</CardTitle>
        </CardHeader>
        <CardContent>
          {payouts.length > 0 ? (
            <div className="space-y-3">
              {payouts.map((payout) => (
                <div 
                  key={payout.id}
                  className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg"
                >
                  <div>
                    <p className="text-white font-medium">{formatMonth(payout.month)}</p>
                    <div className="flex items-center gap-3 text-sm text-gray-400">
                      <span>{payout.points_earned} pts</span>
                      <span>•</span>
                      <span>{(payout.share_percentage * 100).toFixed(1)}% share</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-green-400">
                      ${payout.payout_amount.toFixed(2)}
                    </p>
                    {payout.status === 'paid' || payout.status === 'completed' ? (
                      <Badge className="bg-green-600">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Paid{payout.stripe_transfer_id ? ' via Stripe' : payout.check_number ? ` #${payout.check_number}` : ''}
                      </Badge>
                    ) : payout.status === 'processing' ? (
                      <Badge className="bg-indigo-600">
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        Sending via Stripe
                      </Badge>
                    ) : payout.status === 'failed' ? (
                      <Badge variant="destructive">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        Failed
                      </Badge>
                    ) : payout.status === 'approved' ? (
                      <Badge className="bg-amber-600">
                        <Clock className="w-3 h-3 mr-1" />
                        Approved
                      </Badge>
                    ) : payout.status === 'pending' ? (
                      <Badge variant="secondary">
                        <Clock className="w-3 h-3 mr-1" />
                        Pending
                      </Badge>
                    ) : (
                      <Badge variant="outline">Skipped</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No payout history yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EarningsTab;
